import type { LucideIcon } from "lucide-react";

import { Collapse, Divider, Drawer, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { hasValue, styles } from "@repo/helpers";
import { ArrowLeft, Ban, ChevronDown, ChevronUp, ClipboardList, CornerDownRight, House } from "lucide-react";
import { MouseEvent, Ref, useCallback, useEffect, useMemo, useState } from "react";

import { sectionIsVisible } from "..";
import { sectionType } from "../models/enums";
import { MappedSection } from "../models/mappedComponent";
import style from "./style.module.scss";
import { menuAction, Props } from "./types";

export default function MobileSectionList(props: Props) {
	const [activePages, setActivePages] = useState(() => new Set<string>());
	const [expandedItems, setExpandedItems] = useState(() => new Set<string>());

	useEffect(() => {
		//Active pages is visible item and (if any) parents of visible item
		const activePages = new Set<string>();
		for (const section of props.sections) {
			if (sectionShown(section)) activePages.add(section.linkName);
		}
		setActivePages(activePages);
	}, [props.activePage]);

	useEffect(() => {
		if (!props.shown) setExpandedItems(new Set<string>());
	}, [props.shown]);

	function sectionShown(section: MappedSection) {
		if (section.linkName === props.activePage) return true;
		if (!sectionIsVisible(section)) return false;
		return section.childSections.some(sectionShown);
	}

	return (
		<Drawer anchor="left" open={props.shown} onClose={props.closeList}>
			{renderMenuItems(true)}
			<SectionItemList
				{...props}
				expandedItems={expandedItems}
				setExpandedItems={setExpandedItems}
				activePages={activePages}
			/>
			{renderMenuItems(false)}
		</Drawer>
	);

	function renderMenuItems(top: boolean) {
		if (!props.menuActions?.length) return null;
		const menuItems = props.menuActions.filter((a) => a.top === top);
		if (!menuItems.length) return null;

		return (
			<>
				{!top && <Divider />}
				<List className={styles(!top && style.menuItemList)}>
					{menuItems.sort((a, b) => a.ordinal - b.ordinal).map(renderMenuAction)}
				</List>
				{top && <Divider />}
			</>
		);
	}

	function renderMenuAction(action: menuAction) {
		if (hasValue(action.action))
			return (
				<ListItemButton
					key={action.id}
					disabled={action.disabled}
					onClick={action.action}
					className={styles(action.forceToBottom && style.forceEnd, "!flex-grow-0")}
				>
					{action.icon && (
						<ListItemIcon>
							<ActionIcon name={action.icon} />
						</ListItemIcon>
					)}
					<ListItemText primary={action.name}></ListItemText>
				</ListItemButton>
			);
		return (
			<ListItemButton key={action.id} disabled={action.disabled} onClick={action.action} className="!flex-grow-0">
				{action.icon && (
					<ListItemIcon>
						<ActionIcon name={action.icon} />
					</ListItemIcon>
				)}
				<ListItemText primary={action.name}></ListItemText>
			</ListItemButton>
		);
	}
}

interface State {
	expandedItems: Set<string>;
	activePages: Set<string>;
	setExpandedItems: (i: Set<string>) => void;
}

interface SectionListProps extends Props, State {
	sections: MappedSection[];
	renderingChildSections?: boolean;
	ref?: Ref<any>;
}

function SectionItemList(props: SectionListProps) {
	const { sections, ...rest } = props;
	const { renderingChildSections } = rest;
	return (
		<List className={styles(renderingChildSections && style.childSectionList)}>
			{sections.filter(sectionIsVisible).map((s) => (
				<SectionItem key={s.linkName} {...rest} section={s} />
			))}
		</List>
	);
}

interface SectionItemProps extends Omit<Props, "sections">, State {
	section: MappedSection;
	renderingChildSections?: boolean;
}

function SectionItem(props: SectionItemProps) {
	const { section, ...rest } = props;
	const { renderingChildSections, activePages, activePage, expandedItems, setExpandedItems } = rest;
	const { linkName } = section;
	const childSelected = activePages.has(linkName);
	const selected = props.activePage === linkName || childSelected;

	const childSections = useMemo(() => section.childSections.filter(sectionIsVisible), [section.childSections]);
	const hasChildren =
		childSections.length > 0 &&
		section.section.type !== sectionType.summary &&
		section.section.type !== sectionType.iteratedEditable;
	const sectionExpanded = useCallback(
		function (section: MappedSection) {
			if (section.linkName === activePage) return true;
			if (expandedItems.has(linkName)) return true;
			if (activePages.has(section.linkName)) return true;
			return section.childSections.some(sectionExpanded);
		},
		[linkName, childSections, expandedItems, activePages],
	);

	const expanded = useMemo(() => sectionExpanded(section), [section, sectionExpanded]);

	const sectionEnabled = useCallback(
		function (section: MappedSection) {
			if (section.linkName === activePage) return true;
			if (props.completedSections.includes(section.linkName)) return true;
			return section.childSections.some(sectionEnabled);
		},
		[linkName, section.childSections, props.completedSections],
	);

	const isEnabled = useMemo(() => sectionEnabled(section), [section, sectionEnabled]);

	const showHideExpandedItems = useCallback(() => {
		//If it has a sub-list, either hide or show that list
		const newExpandedItems = new Set(expandedItems);
		if (expanded) newExpandedItems.delete(linkName);
		else newExpandedItems.add(linkName);
		setExpandedItems(newExpandedItems);
	}, [expandedItems, setExpandedItems, linkName]);

	const onListItemClick = useCallback(() => {
		const success = props.jumpToSection(linkName);
		if (!hasChildren || success) props.closeList();
		else {
			showHideExpandedItems();
		}
	}, [props.jumpToSection, linkName, showHideExpandedItems, props.closeList]);

	const onExpandClick = useCallback(
		(e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			showHideExpandedItems();
		},
		[showHideExpandedItems],
	);

	// If a section just holds one child section (i.e., is a simple container),
	// render the child item directly
	if (
		!section.components.length &&
		childSections.length === 1 &&
		section.section.type !== sectionType.iteratedEditable
	) {
		return <SectionItem {...rest} section={childSections[0]} />;
	}

	return (
		<>
			<ListItemButton
				key={linkName}
				selected={selected}
				disabled={!isEnabled}
				onClick={onListItemClick}
				className={styles(
					renderingChildSections && style.childSectionItem,
					hasChildren && expanded && style.listItemWithChildren,
					"!flex-grow-0",
				)}
			>
				<ListItemText primary={<SectionItemText {...props} />} />
				{hasChildren ? (
					expanded ? (
						<ChevronUp onClick={onExpandClick} size={35} />
					) : (
						<ChevronDown onClick={onExpandClick} size={35} />
					)
				) : null}
			</ListItemButton>
			{hasChildren && (
				<Collapse in={expanded} unmountOnExit>
					<SectionItemList {...rest} sections={childSections} renderingChildSections />
				</Collapse>
			)}
		</>
	);
}

function SectionItemText(props: SectionItemProps) {
	const { renderingChildSections, section } = props;

	if (renderingChildSections) {
		return (
			<>
				<CornerDownRight size={20} />
				{`${section.label}`}
			</>
		);
	}

	return <>{section.label}</>;
}

function ActionIcon({ name }: { name: string }) {
	const Icon = resolveMenuIcon(name);
	return Icon ? <Icon /> : null;
}

// menuAction.icon holds a legacy Material Icons name. Add the name here to support another one.
const menuIcons = new Map<string, LucideIcon>([
	["assignment", ClipboardList],
	["home", House],
	["arrow_back", ArrowLeft],
	["block", Ban],
]);

/** Returns the icon for a legacy Material Icons name (trimmed, any casing), or undefined if the name is not known. */
export function resolveMenuIcon(name?: string | null): LucideIcon | undefined {
	if (typeof name !== "string") return undefined;
	return menuIcons.get(name.trim().toLowerCase());
}
